import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as PlusIcon } from 'applicaiton/assets/plus.svg';

import { useAppDispatch, useAppSelector } from 'common/hooks/redux';

import {
  PatientSearch,
  PatientInfo,
  PatientForm,
  FamilyMemberForm,
  MemberInfo,
} from 'features/feature-agenda-reserve-slots-modal/components';

import { PatientProps } from './models';
import {
  Wrapper,
  CreatePatientButton,
  PatientsTabsStyled,
  TabsStyled,
  Tab,
  LeftPatientInfo,
  FamilyMemberTitle,
} from './styles';
import { Dropdown } from 'common/components';
import { setFamilyMemberToAppointment } from 'applicaiton/store/reducers/Appointments/AppoinmentsSlice';
import { Gender } from 'applicaiton/constants/gender';

const Patient: FC<PatientProps> = ({
  setValue,
  errors,
  watchPatient,
  getValuesPatient,
  setValuePatient,
  errorsPatient,
  registerPatient,
  handleSubmitPatient,
  clearErrorsPatient,
  isDirtyPatient,
  resetPatient,
  resetFm,
  setValueFM,
  errorsFM,
  getValuesFM,
  registerFM,
  clearErrorsFM,
  watchFM,
  isCreateAppointmentForFM,
  setIsCreateAppointmentForFM,
  showCreatePatientForm,
  setShowCreatePatientForm,
  hideCreateFamilyMemberButton,
  setHideCreateFamilyMemberButton,
  isEditMode,
  showCreateFamilyMemberForm,
  setShowCreateFamilyMemberForm,
  clearErrors,
  setAddFamilyMemberId,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [currentFamilyMember, setCurrentFamilyMember] = useState('');
  const { currentFamilyMemberSearch, currentPatient, currentAppointment } =
    useAppSelector((state) => state.appointmentsSlice);

  const hasPatientsErrors = !!Object.keys(errorsPatient).length;
  const hasFamilyMemberErrors = !!Object.keys(errorsFM).length;

  const handleShowCreatePatientForm = () => {
    setShowCreatePatientForm(!showCreatePatientForm);
    clearErrors();
  };

  const handleTogglePatientForms = () => {
    setShowCreatePatientForm(!showCreatePatientForm);
    setShowCreateFamilyMemberForm((prev) => !prev);
  };

  const onCancelCreatePatientForm = () => {
    resetPatient();
    resetFm();
    handleShowCreatePatientForm();
    setHideCreateFamilyMemberButton(false);
  };
  const onCancelFamilyMemberForm = () => {
    resetFm();
    handleTogglePatientForms();
    setHideCreateFamilyMemberButton(false);
  };

  const handleShowFamilyMemberTab = () => {
    handleTogglePatientForms();
    setHideCreateFamilyMemberButton(true);
  };

  const handleResetPatient = () => {
    resetPatient();
    resetFm();
    setShowCreatePatientForm(false);
  };

  const showPatientSearch =
    !!currentPatient ||
    !!currentFamilyMemberSearch ||
    (!showCreatePatientForm && !showCreateFamilyMemberForm);
  const showPatientInfo = !!currentPatient || !!currentFamilyMemberSearch;

  const familyMembersOptions = useMemo(() => {
    if (!currentPatient?.familyMembers?.length) {
      return [];
    }

    const options = currentPatient?.familyMembers?.map((item) => {
      return { value: item?.id, label: `${item?.firstName} ${item?.lastName}` };
    });

    return [
      { value: '', label: t('patient.choose_family_member_placeholder') },
      ...options,
    ];
  }, [currentPatient?.familyMembers]);

  useEffect(() => {
    currentAppointment?.bookedForMember?.id &&
      setCurrentFamilyMember(currentAppointment?.bookedForMember?.id);
  }, [currentAppointment]);

  useEffect(() => {
    const filteredMember = currentPatient?.familyMembers?.find(
      (item) => item.id === currentFamilyMember,
    );

    const value =
      filteredMember?.pronunciation === 'Male' ? Gender.Male : Gender.Female;

    setValueFM('firstName', filteredMember?.firstName || '');
    setValueFM('lastName', filteredMember?.lastName || '');
    setValueFM('dateOfBirth', filteredMember?.dateOfBirth || '');
    setValueFM('relationship', filteredMember?.relationship || '');
    setValueFM('fin', filteredMember?.nif || '');
    setValueFM('gender', value);
    setValueFM('ssn', filteredMember?.socialSecurityNumber || '');
  }, [currentFamilyMember]);

  const handleChooseFamilyMember = (id: string) => {
    setCurrentFamilyMember(id);

    const filteredMember = currentPatient?.familyMembers?.find(
      (item) => item.id === id,
    );

    setAddFamilyMemberId(id);

    dispatch(setFamilyMemberToAppointment(filteredMember));
  };

  return (
    <Wrapper>
      {showPatientSearch ? (
        <LeftPatientInfo>
          {!isEditMode && (
            <PatientSearch
              setValue={setValue}
              errorMessage={errors.patientId?.message}
              handleResetPatient={handleResetPatient}
            />
          )}
          {!!currentFamilyMemberSearch && <MemberInfo />}
          {!!currentPatient && !currentFamilyMemberSearch && <PatientInfo />}
          {!isEditMode && (
            <CreatePatientButton
              type="button"
              disabled={showPatientInfo}
              onClick={handleShowCreatePatientForm}
            >
              <PlusIcon /> {t('patient.create_patient')}
            </CreatePatientButton>
          )}
          {isEditMode && (
            <>
              <FamilyMemberTitle>
                {t('patient.choose_family_member_title')}:
              </FamilyMemberTitle>
              {!!familyMembersOptions?.length && (
                <Dropdown
                  id={'familyMembersDropdown'}
                  placeholder={t('patient.choose_family_member_placeholder')}
                  label={t('patient.choose_family_member_label') || ''}
                  value={currentFamilyMember}
                  onChange={(value) => {
                    const id = !Array.isArray(value)
                      ? (value.value as string)
                      : '';

                    handleChooseFamilyMember(id);
                  }}
                  options={familyMembersOptions}
                />
              )}
            </>
          )}
          {isEditMode && !showCreateFamilyMemberForm && (
            <CreatePatientButton
              type="button"
              onClick={handleTogglePatientForms}
            >
              <PlusIcon /> {t('patient.create_family_member')}
            </CreatePatientButton>
          )}
          {showCreateFamilyMemberForm && (
            <FamilyMemberForm
              setValueFM={setValueFM}
              errorsFM={errorsFM}
              getValuesFM={getValuesFM}
              registerFM={registerFM}
              clearErrorsFM={clearErrorsFM}
              watchFM={watchFM}
              onCancelFamilyMemberForm={onCancelFamilyMemberForm}
              isCreateAppointmentForFM={isCreateAppointmentForFM}
              setIsCreateAppointmentForFM={setIsCreateAppointmentForFM}
              showAppointmentForFMCheckbox={false}
            />
          )}
        </LeftPatientInfo>
      ) : (
        <PatientsTabsStyled>
          <TabsStyled>
            <Tab
              type="button"
              isActive={showCreatePatientForm}
              onClick={handleTogglePatientForms}
              hasError={hasPatientsErrors}
            >
              {t('patient.new_patient')}
            </Tab>
            {hideCreateFamilyMemberButton && (
              <Tab
                type="button"
                isActive={showCreateFamilyMemberForm}
                onClick={handleTogglePatientForms}
                hasError={hasFamilyMemberErrors}
              >
                {t('patient.new_family_member')}
              </Tab>
            )}
          </TabsStyled>
          {showCreatePatientForm && (
            <PatientForm
              watchPatient={watchPatient}
              getValuesPatient={getValuesPatient}
              setValuePatient={setValuePatient}
              errorsPatient={errorsPatient}
              registerPatient={registerPatient}
              handleSubmitPatient={handleSubmitPatient}
              clearErrorsPatient={clearErrorsPatient}
              isDirtyPatient={isDirtyPatient}
              onCancelCreatePatientForm={onCancelCreatePatientForm}
              hideCreateFamilyMemberButton={hideCreateFamilyMemberButton}
              handleShowFamilyMemberTab={handleShowFamilyMemberTab}
            />
          )}
          {showCreateFamilyMemberForm && (
            <FamilyMemberForm
              setValueFM={setValueFM}
              errorsFM={errorsFM}
              getValuesFM={getValuesFM}
              registerFM={registerFM}
              clearErrorsFM={clearErrorsFM}
              watchFM={watchFM}
              onCancelFamilyMemberForm={onCancelFamilyMemberForm}
              isCreateAppointmentForFM={isCreateAppointmentForFM}
              setIsCreateAppointmentForFM={setIsCreateAppointmentForFM}
            />
          )}
        </PatientsTabsStyled>
      )}
    </Wrapper>
  );
};

export default Patient;
